import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { LobbyDataGridViewModels } from "./../models/lobby-data-view.request.model";
import { Observable } from "rxjs";
import { AppConfigurations } from "src/app/shared/models/appConfiguration.model";
import { LocationRequestModel } from "../models/locations.request.models";
import { ServiceRequest } from "../models/service.request.model";
import { UserGridRequestModel } from "src/app/admin/user/shared/models/userGridRequestModel";
import { FilterTemplateRequestModel } from "../models/filter-templates.request.model";
import { FilterTemplateResponseModel } from "../models/filter-template.response.model";
import { DetailsViewChartDataRequest } from "../models/chart-data.request.model";
import {
  ReportGridDataRequest,
  ReportExportDataRequest,
} from "../models/grid-data.model";
import { GridDataResult } from "@progress/kendo-angular-grid";
import { map } from "rxjs/operators";
import { AppInitService } from "../../../app-init.service";
import { DatePipe } from "@angular/common";
import { LWAReason } from "src/app/lobby/shared/models/left-without-assistant-reason.model";
import { Store } from "@ngrx/store";
import { State } from "src/app/state/app.state";
import { getLoggedInUserInfo } from "src/app/state";
import { ReportDataViewRequest } from "../models/data-view.model";

declare const window: any;
@Injectable({
  providedIn: "root",
})
export class ReportService {
  public reportControllerUrl: string = "";
  public lobbyDetailViewGridEndpoint: string = "";
  public lobbyDetailViewExportEndpoint: string = "";
  public lobbyEditorGridEndpoint: string = "";
  public lobbyEditorExportEndpoint: string = "";
  public locationDataEndpoint: string = "";
  public getTemplateEndpoint: string = "";
  public getFilterOptionsEndpoint: string = "";
  public getClientOrigDateEndPoint: string = "";
  public getAllChartsDataEndPoint: string = "";
  private appConfig!: AppConfigurations;
  private preferredLanguageId: number = 1;

  constructor(
    public http: HttpClient,
    private appInitService: AppInitService,
    private readonly datePipe: DatePipe,
    private store: Store<State>
  ) {
    appInitService.getAppConfiguration$().subscribe({
      next: (config) => this._onRecieveAppConfig(config),
    });
  }

  private _onRecieveAppConfig(appConfig: AppConfigurations) {
    this.appConfig = appConfig;
    this.reportControllerUrl = `${this.appConfig.reportApi.baseApiUrl}${this.appConfig.reportApi.reportController}`;
  }

  getPreferredLanguage() {
    this.store.select(getLoggedInUserInfo).subscribe((user) => {
      if (user !== undefined && user !== null && user["preferredLanguage"]) {
        this.preferredLanguageId = user["preferredLanguage"];
      }
    });
  }

  private get languageParam(): HttpParams {
    return new HttpParams().set(
      "preferredLanguageId",
      this.preferredLanguageId.toString()
    );
  }

  getChartQueryLookbackYears() {
    return this.appConfig.reportApi.chartDataLookbackYears;
  }
  // ********************************************************************************
  // ************************* Chart related endpoints ******************************
  // ********************************************************************************
  /*
   * Fetches all custom templates belonging to the logged user
   */
  getDetailsViewTemplates(
    templateRequest: FilterTemplateRequestModel
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/filterTemplate?templateId=${templateRequest.TemplateId}&detailViewId=${templateRequest.DetailViewId}`;
    return this.http.get<any>(url);
  }
  /*
   * Saves a template to the logged user's templates
   */
  saveDetailsViewTemplate(
    template: FilterTemplateResponseModel
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/filterTemplate`;
    return this.http.post<any>(url, template);
  }
  /*
   * Deletes a template to the logged user's templates
   */
  deleteDetailsViewTemplate(templateId: number): Observable<any> {
    const url = `${this.reportControllerUrl}/filterTemplate?templateId=${templateId}`;
    return this.http.delete<any>(url);
  }
  // Get Data View Filter
  getDataViewFilter(): Observable<any> {
    this.getPreferredLanguage();
    const url = `${this.reportControllerUrl}/dataViewFilter`
    return this.http.get<any>(url, { params: this.languageParam });
  }
  // Get Data View Grid
  getDataViewGrid(dataViewRequest: ReportDataViewRequest): Observable<any> {
    this.getPreferredLanguage();
    const url = `${this.reportControllerUrl}/dataViewGrid`
    const requestData = {
      ...dataViewRequest,
      startDate: this.toLocalDateString(dataViewRequest.startDate),
      endDate: this.toLocalDateString(dataViewRequest.endDate),
    };
    return this.http.post(url, requestData);
  }
  // Get Data View Grid Export
  getDataViewGridExport(dataViewRequest: ReportDataViewRequest): Observable<any> {
    this.getPreferredLanguage();
    const url = `${this.reportControllerUrl}/dataViewGridExport`
    const requestData = {
      ...dataViewRequest,
      startDate: this.toLocalDateString(dataViewRequest.startDate),
      endDate: this.toLocalDateString(dataViewRequest.endDate),
    };
    return this.http.post(url, requestData, { responseType: 'blob' as 'json', observe: 'response' });
  }

  private toLocalDateString(date: Date): string {
    return this.datePipe.transform(
      date,
      "yyyy-MM-ddT00:00:00.000"
    )! + "Z";
  }
  /*
   * Fetches the full set of possible filteroptions (for populating Filter Options window's drop downs) - as available to the logged user
   */
  getDetailsViewFilterOptions(reportType: number): Observable<any> {
    this.getPreferredLanguage();
    const url = `${this.reportControllerUrl}/filterOptions`;
    const params = this.languageParam.set(
      "detailViewType",
      reportType.toString()
    );
    return this.http.get<any>(url, { params: params });
  }
  /*
   * Fetches the date that the client to which the logged belongs was first placed online - used to set earliest allowed 'from' date in datepicker
   */
  getClientOrigDate(): Observable<any> {
    const url = `${this.reportControllerUrl}/clientOrigDate`;
    return this.http.get<any>(url);
  }
  /*
   * Fetches the data to populate all charts as per the selected filters
   */
  getDetailsViewAllData(
    chartDataRequestModel: DetailsViewChartDataRequest
  ): Observable<any> {
    this.getPreferredLanguage();
    const url = `${this.reportControllerUrl}/lobbyDetailViewChartsData`;
    const queryStartDate =
      this.datePipe.transform(
        chartDataRequestModel.startDate,
        "yyyy-MM-ddT00:00:00.000"
      )! + "Z";
    const queryEndDate =
      this.datePipe.transform(
        chartDataRequestModel.endDate,
        "yyyy-MM-ddT23:59:59.999"
      )! + "Z";
    const chartDataRequestModelDto = {
      ...chartDataRequestModel,
      startDate: queryStartDate,
      endDate: queryEndDate,
    };
    return this.http.post(url, chartDataRequestModelDto, {
      params: this.languageParam,
    });
  }
  // Fetches all data for data grid according to the current selected filter
  getLobbyDetailViewGrid(
    gridDataRequest: ReportGridDataRequest
  ): Observable<any> {
    this.getPreferredLanguage();
    const url = `${this.reportControllerUrl}/lobbyDetailViewGrid`;
    const queryStartDate =
      this.datePipe.transform(
        gridDataRequest.startDate,
        "yyyy-MM-ddT00:00:00.000"
      )! + "Z";
    const queryEndDate =
      this.datePipe.transform(
        gridDataRequest.endDate,
        "yyyy-MM-ddT23:59:59.999"
      )! + "Z";
    const requestDto = {
      ...gridDataRequest,
      startDate: queryStartDate,
      endDate: queryEndDate,
    };
    return this.http.post<any>(url, requestDto, { params: this.languageParam });
  }
  // Fetches all data for export according to the current selected filters
  getLobbyDetailViewExport(
    exportDataRequest: ReportExportDataRequest
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/lobbyDetailViewExport`;
    return this.http.post<any>(url, exportDataRequest).pipe(
      map(
        (response) =>
          <GridDataResult>{
            data: response,
            total: response.length,
          }
      )
    );
  }
  // Fetches all data for csv export according to the current selected filters
  getLobbyDetailViewCsvExport(
    exportDataRequest: ReportExportDataRequest
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/lobbyDetailViewCsvExport`;
    const queryStartDate =
      this.datePipe.transform(
        exportDataRequest.startDate,
        "yyyy-MM-ddT00:00:00.000"
      )! + "Z";
    const queryEndDate =
      this.datePipe.transform(
        exportDataRequest.endDate,
        "yyyy-MM-ddT23:59:59.999"
      )! + "Z";
    const exportDataRequestDto = {
      ...exportDataRequest,
      startDate: queryStartDate,
      endDate: queryEndDate,
    };
    return this.http.post<any>(url, exportDataRequestDto, {
      responseType: "blob" as "json",
      observe: "response",
    });
  }
  // Fetches all data for data grid according to the current selected filter
  getLobbyEditorGrid(gridDataRequest: ReportGridDataRequest): Observable<any> {
    const url = `${this.reportControllerUrl}/lobbyEditorGrid`;
    const queryStartDate =
      this.datePipe.transform(
        gridDataRequest.startDate,
        "yyyy-MM-ddT00:00:00.000"
      )! + "Z";
    const queryEndDate =
      this.datePipe.transform(
        gridDataRequest.endDate,
        "yyyy-MM-ddT23:59:59.999"
      )! + "Z";
    const requestDto = {
      ...gridDataRequest,
      startDate: queryStartDate,
      endDate: queryEndDate,
    };
    return this.http.post<any>(url, requestDto);
  }
  // Fetches all data for export according to the current selected filters
  getLobbyEditorExport(
    exportDataRequest: ReportExportDataRequest
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/lobbyEditorExport`;
    return this.http.post<any>(url, exportDataRequest).pipe(
      map(
        (response) =>
          <GridDataResult>{
            data: response,
            total: response.length,
          }
      )
    );
  }
  // Fetches all data for csv export according to the current selected filters
  getLobbyEditorCsvExport(
    exportDataRequest: ReportExportDataRequest
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/lobbyEditorCsvExport`;
    const queryStartDate =
      this.datePipe.transform(
        exportDataRequest.startDate,
        "yyyy-MM-ddT00:00:00.000"
      )! + "Z";
    const queryEndDate =
      this.datePipe.transform(
        exportDataRequest.endDate,
        "yyyy-MM-ddT23:59:59.999"
      )! + "Z";
    const requestDto = {
      ...exportDataRequest,
      startDate: queryStartDate,
      endDate: queryEndDate,
    };
    return this.http.post<any>(url, requestDto, {
      responseType: "blob" as "json",
      observe: "response",
    });
  }

  // Fetches all data for csv export according to the current selected filters
  getStaffSignInOutLogCsvExportCsvExport(
    exportDataRequest: ReportExportDataRequest
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/staffSignInOutLogCsvExport`;
    return this.http.post<any>(url, exportDataRequest, {
      responseType: "blob" as "json",
    });
  }

  // ********************************************************************************
  // ************************* Grid related endpoints ******************************
  // ********************************************************************************

  /**
   * Loads users data to the Users Grid
   * -1=All
   *  1=Active
   *  0=Inactive
   */
  public getUsersForTheGrid(
    userGridRequest: UserGridRequestModel
  ): Observable<any> {
    const url = `${this.reportControllerUrl}/users?status=${userGridRequest.Status}
    &locationId=${userGridRequest.LocationId}
    &IncludeSubLocation=${userGridRequest.IncludeSubLocations}
    &sortInfo.sortColumn=${userGridRequest.SortInfo.SortColumn}
    &sortInfo.sortDirection=${userGridRequest.SortInfo.sortDirection}
    &pageInfo.page=${userGridRequest.PageInfo.Page}
    &pageInfo.pageSize=${userGridRequest.PageInfo.PageSize}
    &search=${userGridRequest.SearchText}`;
    return this.http.get<any>(url);
  }
  /*
   * Fetches all services for a location services dropdown
   */
  getAllProductOrServices(serviceRequest: ServiceRequest): Observable<any> {
    const url = `${this.reportControllerUrl}/${serviceRequest.locationId}/services?showInKiosk=${serviceRequest.showInKiosk}&status=${serviceRequest.sortByDisplayName}`;
    return this.http.get<any>(url);
  }
  /*
   *  Gets all data for DataViewReport
   */
  getDataForDataViewReport(request?: LobbyDataGridViewModels): Observable<any> {
    const url = `${this.reportControllerUrl}/lobbyReport`;
    return this.http.post<any>(url, request);
  }
  /*
   * Fetches all services category for a location
   */
  getAllProductAndServiceCategory(locationCode: string): Observable<any> {
    const url = `${this.reportControllerUrl}/serviceCategory?locationCode=${locationCode}`;
    return this.http.get<any>(url);
  }
  getAllLocation(locationRequest: LocationRequestModel): Observable<any> {
    const url = `${this.reportControllerUrl}/locationsData?type=${locationRequest.Type}&status=${locationRequest.Status}&key=${locationRequest.Key}&sortInfo.sortColumn=${locationRequest.SortInfo.SortColumn}&sortInfo.sortDirection=${locationRequest.SortInfo.sortDirection}&pageInfo.page=${locationRequest.PageInfo.Page}&pageInfo.pageSize=${locationRequest.PageInfo.PageSize}`;
    return this.http.get<any>(url);
  }
  updateLobbyEntry(lobbyEntryId: number, lobbyRecord: any): Observable<any> {
    return this.http.put<any>(
      `${this.reportControllerUrl}/${lobbyEntryId}/update`,
      lobbyRecord
    );
  }
  getLwaReasons(): Observable<LWAReason[]> {
    const url = `${this.reportControllerUrl}/lwareasons`;
    return this.http.get<any>(url);
  }
}
